import React from 'react';
import cx from 'classnames';
import css from './AISettingsPanel.module.scss';

// AI 设置面板 —— 从 BOControlPanel 的齿轮按钮弹出
//   - 顶部大开关：AI 服务总开关
//   - 三块卡片：青铜 / 白银 / 黄金，每块里有该 tier 的子功能 toggle
//   - 白银 / 黄金当前锁定，显示"开通解锁"按钮（暂时只是 UI，未来接订阅）

const BRONZE_FEATURES = [
  { key: 'priorityHint', name: '优先级提示',   desc: '基于赛事 PR/BR 数据推荐该 ban / 该 pick 的英雄' },
  { key: 'roleGapScan',  name: '位置漏洞扫描', desc: '检测阵容缺前排 / 缺控 / 伤害单一等结构问题' },
  { key: 'counterPick',  name: 'Counter Pick',  desc: '对方某位选定后，推荐 3-5 个克制英雄' },
  { key: 'compScore',    name: '阵容综合评分', desc: '双方早期 / 中期 / 后期 1-10 分对比' },
  { key: 'opWarning',    name: 'OP 警告',       desc: '版本 Top OP 英雄还未 ban/pick 时实时提醒' },
];

const SILVER_FEATURES = [
  { key: 'winRatePredict',  name: '胜率预测',     desc: '实时滚动胜率（基于 LLM）' },
  { key: 'teamComposition', name: '阵容标签识别', desc: '自动识别 poke / 1-3-1 / dive 等战术' },
  { key: 'powerSpike',      name: '强势期分析',   desc: '15-25 分钟 power spike 时间窗' },
  { key: 'nextPickAI',      name: '下一手推荐',   desc: 'AI 给 Top 3 候选 + 文字理由' },
  { key: 'autoReplay',      name: '复盘报告',     desc: '赛后自动生成 markdown 复盘' },
];

const GOLD_FEATURES = [
  { key: 'opponentProfile', name: '对手画像',     desc: '教练 BP 风格 / 偏好阵容' },
  { key: 'playerPool',      name: '选手英雄池',   desc: '对方选手历史英雄 + 胜率' },
  { key: 'whatIf',          name: 'What-If 推演', desc: '假设某一步改选，胜率变化' },
  { key: 'similarBP',       name: '历史相似 BP',  desc: '匹配职业赛历史 BP 复盘' },
  { key: 'aiChat',          name: 'AI 教练对话',   desc: '自然语言问答（"为什么 P3 选 Hwei"）' },
];

const AISettingsPanel = ({ aiSettings, onClose }) => {
  const { settings, toggleEnabled, toggleFeature, isLocked } = aiSettings;

  return (
    <div className={css.Backdrop} onClick={onClose}>
      <div className={css.Panel} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={css.Header}>
          <div>
            <div className={css.Title}>AI 教练助手</div>
            <div className={css.Subtitle}>智能 BP 建议 · 阵容分析 · 战术推演</div>
          </div>
          <button type="button" className={css.CloseBtn} onClick={onClose} aria-label="关闭">
            ×
          </button>
        </div>

        {/* 总开关 */}
        <div className={css.MasterRow}>
          <div className={css.MasterText}>
            <div className={css.MasterLabel}>
              {settings.enabled ? 'AI 服务已开启' : 'AI 服务未开启'}
            </div>
            <div className={css.MasterDesc}>
              {settings.enabled
                ? '所有授权功能正在为你的 BP 决策提供建议'
                : '开启后即可使用本机青铜套餐，无需联网，零费用'}
            </div>
          </div>
          <label className={cx(css.Switch, settings.enabled && css.SwitchOn)}>
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={toggleEnabled}
            />
            <span className={css.SwitchSlider}>
              <span className={css.SwitchKnob} />
            </span>
          </label>
        </div>

        {/* 套餐：青铜 */}
        <TierBlock
          tier="bronze"
          title="青铜套餐"
          subtitle="本机规则计算 · 已包含"
          accent="bronze"
          locked={isLocked('bronze')}
          features={BRONZE_FEATURES}
          settings={settings}
          enabled={settings.enabled}
          onToggle={toggleFeature}
        />

        {/* 套餐：白银 */}
        <TierBlock
          tier="silver"
          title="白银套餐"
          subtitle="接入大模型 · 即将上线"
          accent="silver"
          locked={isLocked('silver')}
          features={SILVER_FEATURES}
          settings={settings}
          enabled={settings.enabled}
          onToggle={toggleFeature}
        />

        {/* 套餐：黄金 */}
        <TierBlock
          tier="gold"
          title="黄金套餐"
          subtitle="战队级数据库 · 联系销售"
          accent="gold"
          locked={isLocked('gold')}
          features={GOLD_FEATURES}
          settings={settings}
          enabled={settings.enabled}
          onToggle={toggleFeature}
        />

        <div className={css.Footer}>
          AI 推荐仅供参考，最终决策权在教练
        </div>
      </div>
    </div>
  );
};

const TierBlock = ({ tier, title, subtitle, accent, locked, features, settings, enabled, onToggle }) => {
  return (
    <div className={cx(css.Tier, css[`Tier_${accent}`], locked && css.TierLocked)}>
      <div className={css.TierHeader}>
        <div>
          <div className={css.TierTitle}>
            {title}
            {locked && <span className={css.LockChip}>🔒 锁定</span>}
          </div>
          <div className={css.TierSubtitle}>{subtitle}</div>
        </div>
        {locked && (
          <button type="button" className={css.UnlockBtn} disabled>
            开通解锁
          </button>
        )}
      </div>

      <div className={css.FeatureList}>
        {features.map((f) => {
          const checked = !!(settings[tier] && settings[tier][f.key]);
          const interactive = enabled && !locked;
          return (
            <label key={f.key} className={cx(css.FeatureRow, !interactive && css.FeatureRowDisabled)}>
              <input
                type="checkbox"
                checked={checked}
                disabled={!interactive}
                onChange={() => onToggle(tier, f.key)}
              />
              <span className={css.FeatureCheck} />
              <span className={css.FeatureBody}>
                <span className={css.FeatureName}>{f.name}</span>
                <span className={css.FeatureDesc}>{f.desc}</span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default AISettingsPanel;
